const toPositiveNumber = (value) => {
  const number = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const minPositive = (values) => {
  const validValues = values
    .map(toPositiveNumber)
    .filter((value) => value !== null);

  return validValues.length > 0 ? Math.min(...validValues) : null;
};

const getPackagePrices = (packages) => {
  if (!Array.isArray(packages)) return [];

  return packages.flatMap((pkg) => {
    if (!pkg?.prices) return [];

    const durationHours = Number(pkg.durationHours);
    const divisor = Number.isFinite(durationHours) && durationHours > 0 ? durationHours : 1;

    if (Array.isArray(pkg.prices)) {
      return pkg.prices.map((entry) => entry?.price != null ? entry.price / divisor : null);
    }

    if (typeof pkg.prices === 'object') {
      return Object.values(pkg.prices).map((price) => price != null ? price / divisor : null);
    }

    return [];
  });
};

const getTierPrices = (studio) => {
  const backendTiers = Array.isArray(studio?.tieredPricing) ? studio.tieredPricing : [];
  const editorGroups = Array.isArray(studio?.priceGroups) ? studio.priceGroups : [];

  return [...backendTiers, ...editorGroups].map((tier) => (
    tier?.pricePerHour ?? tier?.price
  ));
};

export const getStudioLowestPrice = (studio) => {
  if (!studio) return null;

  const isPackagePricing =
    studio.pricingMode === 'PACKAGES' ||
    studio.pricingType === 'paketpreise';

  if (isPackagePricing) {
    return minPositive(getPackagePrices(studio.packages));
  }

  const hasTieredPricing =
    studio.pricingMode === 'HOURLY' ||
    studio.pricingType === 'stundenpreis' ||
    studio.pricingMode == null;

  if (hasTieredPricing) {
    const tierPrice = minPositive(getTierPrices(studio));
    if (tierPrice !== null) return tierPrice;
  }

  return toPositiveNumber(studio.pricePerHour);
};

export const getStudioPriceDisplay = (studio) => {
  const price = getStudioLowestPrice(studio);
  if (price === null) {
    return {
      price: null,
      label: '',
      qualifier: '',
      unit: '',
    };
  }

  const isPackagePricing =
    studio?.pricingMode === 'PACKAGES' ||
    studio?.pricingType === 'paketpreise';

  const hasTieredPricing =
    !isPackagePricing &&
    (
      (Array.isArray(studio?.tieredPricing) && studio.tieredPricing.length > 0) ||
      (studio?.adjustPriceByPeople && Array.isArray(studio?.priceGroups) && studio.priceGroups.length > 0)
    );

  const qualifier = isPackagePricing || hasTieredPricing ? 'ab' : '';
  const displayPrice = Math.round(price);

  return {
    price,
    label: `${qualifier ? `${qualifier} ` : ''}${displayPrice}€`,
    qualifier,
    unit: 'pro Stunde',
  };
};
